import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { fileUrl } from "@/lib/files";
import { fmtDate, fmtDA } from "@/lib/format";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { DouaneDialog } from "@/components/DouaneDialog";
import { D10Recap } from "@/components/D10Recap";
import { DouaneImport } from "@/components/DouaneImport";
import { Ship, Plus, Pencil, Trash2, Eye, Receipt, Loader2 } from "lucide-react";

const COLS = {
  d10: [["N° D10", (d) => d.numero], ["Date", (d) => fmtDate(d.date)], ["Marchandise", (d) => d.marchandise || "—"], ["Valeur douane", (d) => fmtDA(d.valeur_douane), 1],
    ["Droits", (d) => fmtDA(d.droits_douane), 1], ["TVA", (d) => fmtDA(d.tva), 1], ["Quittance", (d) => d.quittance_numero ? `${d.quittance_numero} ${d.quittance_date ? fmtDate(d.quittance_date) : ""}` : "—"],
    ["Entrée de stock", (d) => d.entree_stock ? `${fmtDate(d.entree_stock.split("|")[0])} ${d.entree_stock.split("|")[1]}` : "—"]],
  transitaire: [["N° facture", (d) => d.numero], ["Date", (d) => fmtDate(d.date)], ["Transitaire", (d) => d.tiers || "—"], ["Marchandise", (d) => d.marchandise || "—"],
    ["HT", (d) => fmtDA(d.montant_ht), 1], ["TVA", (d) => fmtDA(d.tva), 1], ["TTC", (d) => fmtDA((d.montant_ht || 0) + (d.tva || 0)), 1]],
  frais: [["N° facture", (d) => d.numero], ["Date", (d) => fmtDate(d.date)], ["Type", (d) => d.type_frais || "—"], ["Fournisseur", (d) => d.tiers || "—"],
    ["HT", (d) => fmtDA(d.montant_ht), 1], ["TVA", (d) => fmtDA(d.tva), 1], ["TTC", (d) => fmtDA((d.montant_ht || 0) + (d.tva || 0)), 1]],
};

const Row = ({ d, kind, editable, onEdit, onDelete }) => (
  <tr data-testid={`douane-row-${d.id}`}>
    <td className="py-2 pr-2 text-xs">{d.societe_nom}</td>
    {COLS[kind].map(([h, f, r]) => <td key={h} className={`py-2 pr-2 ${r ? "text-right font-mono whitespace-nowrap" : ""}`}>{f(d)}</td>)}
    <td className="py-2 text-right whitespace-nowrap">
      {d.file_id && <Button size="sm" variant="outline" className="gap-1" onClick={() => window.open(fileUrl(d.file_id), "_blank")} data-testid={`douane-view-${d.id}`}><Eye className="h-4 w-4" /> {kind === "d10" ? "D10" : "Voir"}</Button>}
      {d.quittance_file_id && <Button size="sm" variant="outline" className="gap-1 ml-1" onClick={() => window.open(fileUrl(d.quittance_file_id), "_blank")} data-testid={`douane-view-recu-${d.id}`}><Receipt className="h-4 w-4" /> Reçu</Button>}
      {editable && <>
        <Button size="sm" variant="ghost" onClick={() => onEdit(d)} data-testid={`douane-edit-${d.id}`}><Pencil className="h-4 w-4" /></Button>
        <Button size="sm" variant="ghost" onClick={() => onDelete(d)} data-testid={`douane-delete-${d.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
      </>}
    </td>
  </tr>
);

export default function Douane({ kind, title, subtitle }) {
  const { societeId } = useApp();
  const { role } = useAuth();
  const editable = role === "administrateur" || role === "comptabilite";
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/douane", { params: { kind, societe_id: societeId || "all" } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { setItems(null); load(); }, [kind, societeId]); // eslint-disable-line

  const onDelete = async (d) => {
    if (!window.confirm(`Supprimer ${d.numero} ? La facture fournisseur liée et sa TVA dans le G50 seront aussi supprimées.`)) return;
    try {
      await api.delete(`/douane/${d.id}`);
      toast.success("Supprimé");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div data-testid={`douane-page-${kind}`}>
      <PageHeader title={title} subtitle={subtitle}
        actions={editable && <div className="flex flex-wrap gap-2"><DouaneImport kind={kind} societeId={societeId} onDone={load} /><Button className="gap-1" onClick={() => setEdit({})} data-testid="douane-add-btn"><Plus className="h-4 w-4" /> Ajouter</Button></div>} />
      <div className="rounded-xl border bg-card p-5">
        {items === null ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          : !items.length ? <EmptyState icon={Ship} title="Aucun document" hint="La TVA saisie est reprise automatiquement dans le G50 du mois (TVA déductible)." />
            : <div className="overflow-x-auto"><table className="w-full text-sm min-w-[1100px]">
              <thead><tr className="text-left text-muted-foreground border-b"><th className="py-2 pr-2 font-medium">Société</th>
                {COLS[kind].map(([h, , r]) => <th key={h} className={`py-2 pr-2 font-medium ${r ? "text-right" : ""}`}>{h}</th>)}<th /></tr></thead>
              <tbody className="divide-y">{items.map((d) => <Row key={d.id} d={d} kind={kind} editable={editable} onEdit={setEdit} onDelete={onDelete} />)}</tbody>
            </table></div>}
      </div>
      {kind === "d10" && <D10Recap societeId={societeId} refresh={items} />}
      {edit && <DouaneDialog kind={kind} doc={edit.id ? edit : null} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
